import React, { useEffect, useState } from "react";
import ExcelJS from "exceljs";
import Styles from "./RequisitionDetailPanel.module.css";
import PreviewModal from "../../../../commons/PreviewModal/PreviewModal";
import { getDocumentUrlByLedgerId } from "../../../../Services/ApiCalling/banking";
import { formatInr, signed, STATUS_META, formatDate, formatDateDash, connectionStatusMeta } from "../../utils/reconFormat";

// Excel sheet names cap at 31 chars and disallow : \ / ? * [ ].
const safeSheetName = (name) => name.replace(/[:\\/?*[\]]/g, " ").slice(0, 31);

const HEADER_BG = "FFF1935C";
const HEADER_TEXT = "FFFFFFFF";
const BORDER = { style: "thin", color: { argb: "FFE0E0E0" } };

const fill = (cell, argb) => {
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb } };
};
const border = (cell) => {
  cell.border = { top: BORDER, bottom: BORDER, left: BORDER, right: BORDER };
};

const addPeriodSheet = (workbook, name, periods, showBf) => {
  if (!periods?.length) return;
  const sheet = workbook.addWorksheet(safeSheetName(name));

  const columns = [
    { header: "Start", key: "start", width: 14 },
    { header: "End", key: "end", width: 14 },
    { header: "Quantum (MW)", key: "quantum", width: 16 },
    ...(showBf ? [{ header: "Banking Factor %", key: "bf", width: 16 }] : []),
    { header: "Basis", key: "basis", width: 32 },
  ];
  sheet.columns = columns;

  const headerRow = sheet.getRow(1);
  headerRow.height = 24;
  columns.forEach((_, i) => {
    const cell = headerRow.getCell(i + 1);
    fill(cell, HEADER_BG);
    cell.font = { bold: true, size: 11, color: { argb: HEADER_TEXT } };
    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    border(cell);
  });

  periods.forEach((p, i) => {
    const rowNum = i + 2;
    const row = sheet.getRow(rowNum);
    row.height = 20;
    const bg = i % 2 === 0 ? "FFFFFFFF" : "FFF6F8FA";

    const values = [
      p.start,
      p.end,
      p.quantum_mw ?? 0,
      ...(showBf ? [p.banking_factor_pct ?? 0] : []),
      p.basis_raw || p.basis || "—",
    ];
    values.forEach((value, i2) => {
      const cell = row.getCell(i2 + 1);
      cell.value = value;
      fill(cell, bg);
      cell.font = { size: 10.5 };
      cell.alignment = { horizontal: i2 <= 1 ? "left" : "center", vertical: "middle" };
      if (i2 === 2) cell.numFmt = "#,##0.00";
      border(cell);
    });
  });
};

const downloadSupplyReturnWorkbook = async (row, contract) => {
  const workbook = new ExcelJS.Workbook();
  addPeriodSheet(workbook, "Supply Periods", contract?.supply_periods, false);
  addPeriodSheet(workbook, "Return Periods", contract?.return_periods, true);

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const filename = `${row?.name || "requisition"}_supply_return.xlsx`.replace(
    /[^a-zA-Z0-9_\-.]/g,
    "_",
  );
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

const STATUS_CAPTION = {
  RECONCILED: "Fully reconciled — no balance remaining against this requisition.",
  BALANCE_LEFT: "Balance still remains to be drawn against this requisition.",
  DRAWAL_PENDING: "Drawal against this requisition is yet to begin.",
  OVER_DRAWN: "More has been drawn than the drawable quantum allows.",
};

const RequisitionDetailPanel = ({ row, onClose, onExport, contract }) => {
  const [activeTab, setActiveTab] = useState("CONTRACT");
  const [loaPreviewOpen, setLoaPreviewOpen] = useState(false);
  const [loaPreviewUrl, setLoaPreviewUrl] = useState(null);

  useEffect(() => {
    setActiveTab("CONTRACT");
    setLoaPreviewOpen(false);
    setLoaPreviewUrl(null);
  }, [row]);

  const handleViewLoa = async () => {
    try {
      const response = await getDocumentUrlByLedgerId(contract.doc_id);
      const url = response?.data;
      if (url) {
        setLoaPreviewUrl(url);
        setLoaPreviewOpen(true);
      }
    } catch (err) {
      console.error("Could not fetch LOA document URL.", err);
    }
  };

  if (!row) return null;

  const meta = STATUS_META[row.status];
  const drawn = row.drawn || 0;
  const drawnPct =
    row.drawable > 0 ? Math.round(Math.min(100, Math.max(0, (drawn / row.drawable) * 100))) : 0;

  return (
    <>
      <div className={Styles.overlay} onClick={onClose} />
      <aside className={Styles.panel}>
        <div className={Styles.header}>
          <div className={Styles.headerText}>
            <div className={Styles.eyebrow}>REQUISITION DETAIL</div>
            <div className={Styles.title} title={row.name}>{row.name}</div>
            {row.contractId && (
              <div className={Styles.contractId} title={`Contract ID: ${row.contractId}`}>
                Contract ID: {row.contractId}
              </div>
            )}
          </div>
          <div className={Styles.headerActions}>
            {activeTab === "SUPPLY_RETURN" &&
            (contract?.supply_periods?.length || contract?.return_periods?.length) ? (
              <button
                type="button"
                className={Styles.downloadBtn}
                onClick={() => downloadSupplyReturnWorkbook(row, contract)}
                aria-label="Download supply and return data"
                title="Download supply & return (Excel)"
              >
                <span className={Styles.downloadIcon}>⬇</span>
                Download
              </button>
            ) : null}
            {activeTab === "CONTRACT" && contract ? (
              <>
                {contract.has_pdf && contract.doc_id && (
                  <button
                    type="button"
                    className={Styles.viewLoaBtn}
                    onClick={handleViewLoa}
                    aria-label="View uploaded LOA"
                    title="View uploaded LOA"
                  >
                    View LOA
                  </button>
                )}
              </>
            ) : null}
            <button type="button" className={Styles.closeBtn} onClick={onClose} aria-label="Close">
              ×
            </button>
          </div>
        </div>

        <div className={Styles.tabRow}>
          <button
            type="button"
            className={`${Styles.tabBtn} ${activeTab === "CONTRACT" ? Styles.tabBtnActive : ""}`}
            onClick={() => setActiveTab("CONTRACT")}
          >
            Contract
          </button>
          <button
            type="button"
            className={`${Styles.tabBtn} ${activeTab === "SUPPLY_RETURN" ? Styles.tabBtnActive : ""}`}
            onClick={() => setActiveTab("SUPPLY_RETURN")}
          >
            Supply &amp; Return
          </button>
          {/* <button
            type="button"
            className={`${Styles.tabBtn} ${activeTab === "DETAILS" ? Styles.tabBtnActive : ""}`}
            onClick={() => setActiveTab("DETAILS")}
          >
            Details
          </button> */}
        </div>

        {false && activeTab === "DETAILS" && (
          <>
            <div className={Styles.summaryCard}>
              <div className={Styles.ring} style={{ "--pct": drawnPct, "--ringColor": meta.color }}>
                <div className={Styles.ringInner}>
                  <div className={Styles.ringPct}>{drawnPct}%</div>
                  <div className={Styles.ringLabel}>drawn</div>
                </div>
              </div>
              <div>
                <span className={Styles.statusPill} style={{ color: meta.color, background: `${meta.color}1a` }}>
                  <span className={Styles.pillDot} style={{ background: meta.color }} />
                  {meta.label}
                </span>
                <div className={Styles.statusCaption}>{STATUS_CAPTION[row.status]}</div>
              </div>
            </div>

            <div className={Styles.sectionLabel}>BANKING FACTOR CALCULATION</div>
            <div className={Styles.calcRows}>
              <div className={Styles.calcRow}>
                <span>Injected</span>
                <strong>{formatInr(row.injected)} MWh</strong>
              </div>
              <div className={Styles.calcRow}>
                <span>Banking Factor</span>
                <strong className={Styles.bfValue}>{row.bf != null ? `${row.bf}%` : "—"}</strong>
              </div>
              <div className={Styles.calcRow}>
                <span>Drawable Quantum</span>
                <strong>
                  {formatInr(row.injected)} × {row.bf != null ? `${row.bf}%` : "—"} = {formatInr(row.drawable)}
                </strong>
              </div>
              <div className={Styles.calcRow}>
                <span>Actual Drawn</span>
                <strong>{row.drawn == null ? "—" : `${formatInr(row.drawn)} MWh`}</strong>
              </div>
              <div className={`${Styles.calcRow} ${Styles.balanceRow}`}>
                <span>Balance</span>
                <strong style={{ color: meta.color }}>{signed(row.remaining)} MWh</strong>
              </div>
            </div>

            <div className={Styles.periodRow}>
              <div className={Styles.periodCard}>
                <div className={Styles.periodLabel}>INJECTION PERIOD</div>
                <div className={Styles.periodValue}>{row.injDates?.replace(/^Inj\s*/, "") || "—"}</div>
              </div>
              <div className={Styles.periodCard}>
                <div className={Styles.periodLabel}>DRAWAL PERIOD</div>
                <div className={Styles.periodValue}>{row.drawDates?.replace(/^Draw\s*/, "") || "—"}</div>
              </div>
            </div>
          </>
        )}

        {activeTab === "SUPPLY_RETURN" && <SupplyReturnTab contract={contract} />}

        {activeTab === "CONTRACT" && <ContractTab contract={contract} />}

        {/* {activeTab === "DETAILS" && (
          <button type="button" className={Styles.exportBtn} onClick={() => onExport?.(row)}>
            ⬇ Export requisition statement
          </button>
        )} */}
      </aside>

      <PreviewModal
        open={loaPreviewOpen}
        onClose={() => setLoaPreviewOpen(false)}
        fileUrl={loaPreviewUrl}
        documentId={contract?.doc_id}
        fileName="Letter of Authorization"
      />
    </>
  );
};

const ContractRow = ({ label, value, emphasize }) => (
  <div className={Styles.contractRow}>
    <span className={Styles.contractRowLabel}>{label}</span>
    <span className={`${Styles.contractRowValue} ${emphasize ? Styles.contractRowValueStrong : ""}`}>
      {value ?? "—"}
    </span>
  </div>
);

const contractTraders = (contract) =>
  Array.isArray(contract.traders) && contract.traders.length > 0
    ? contract.traders.join(", ")
    : contract.trader || null;

const PeriodTable = ({ title, rows, showBf }) => {
  const totalQuantum = rows?.reduce((acc, p) => acc + (p.quantum_mw || 0), 0);
  const gridCols = showBf ? "1.4fr 1fr 0.8fr 1.3fr" : "1.4fr 1fr 1.6fr";

  return (
    <div className={Styles.dailySection}>
      <div className={Styles.sectionHeading}>
        <span className={Styles.sectionDot} />
        <span className={Styles.sectionLabel}>{title}</span>
        {!!rows?.length && (
          <span className={Styles.periodTotalTag}>{formatInr(totalQuantum)} MW total</span>
        )}
      </div>
      {!rows || rows.length === 0 ? (
        <div className={Styles.emptyState}>No periods available</div>
      ) : (
        <div className={Styles.dailyTableWrap}>
          <div className={Styles.dailyBody}>
            <div className={Styles.dailyHeadRow} style={{ gridTemplateColumns: gridCols }}>
              <span>Period</span>
              <span className={Styles.numCol}>Quantum</span>
              {showBf && <span className={Styles.numCol}>BF</span>}
              <span>Basis</span>
            </div>
            {rows.map((p) => (
              <div key={p.id} className={Styles.dailyCard}>
                <div className={Styles.dailyDataRow} style={{ gridTemplateColumns: gridCols }}>
                  <span className={Styles.dateValue}>
                    {formatDateDash(p.start)} – {formatDateDash(p.end)}
                  </span>
                  <span className={`${Styles.valueCell} ${Styles.numCol}`}>
                    {formatInr(p.quantum_mw)} MW{p.quantum_is_max ? " max" : ""}
                  </span>
                  {showBf && (
                    <span className={Styles.numCol}>
                      <span className={`${Styles.badge} ${Styles.badgeNeutral}`}>
                        {p.banking_factor_pct != null ? `${p.banking_factor_pct}%` : "—"}
                      </span>
                    </span>
                  )}
                  <span className={Styles.approvalLine}>{p.basis_raw || p.basis || "—"}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const ContractTab = ({ contract }) => {
  if (!contract) {
    return <div className={Styles.emptyState}>No matching active contract found for this connection.</div>;
  }

  const status = connectionStatusMeta(contract.connection_status);

  return (
    <div className={Styles.supplyReturnTab}>
      <div className={Styles.contractCard}>
        <div className={Styles.contractCardTop}>
          <ContractRow label="Company" value={contract.company_display_name} />
          <span
            className={Styles.statusPill}
            style={{ color: status.color, background: `${status.color}1a` }}
          >
            <span className={Styles.pillDot} style={{ background: status.color }} />
            {status.label}
          </span>
        </div>
        <ContractRow label="Supplier" value={contract.supplier} emphasize />
        <ContractRow label="Receiver" value={contract.receiver} emphasize />
        <ContractRow label="LOA Number" value={contract.loa_number} emphasize />
        <ContractRow label="LOA Date" value={formatDate(contract.loa_date)} />
        <ContractRow label="Trader(s)" value={contractTraders(contract)} />
      </div>
    </div>
  );
};

const SupplyReturnTab = ({ contract }) => (
  <div className={Styles.supplyReturnTab}>
    <PeriodTable title="SUPPLY PERIODS" rows={contract?.supply_periods} showBf={false} />
    <PeriodTable title="RETURN PERIODS" rows={contract?.return_periods} showBf />
  </div>
);

export default RequisitionDetailPanel;
